import React, { useState, useEffect, useRef } from 'react';
import { 
  Activity, CalendarClock, Stethoscope, HeartHandshake, 
  Microscope, FileText, Phone, Video, Send, 
  Paperclip, ShieldCheck, CheckCircle2, ChevronRight,
  Battery, Wifi, Signal, ChevronLeft, User, MessageSquare,
  AlertTriangle, BrainCircuit, LineChart, Baby, Pill,
  Clock, CheckCheck, XCircle, Search, FileDown,
  Plus, Mic, Image as ImageIcon
} from 'lucide-react';

// ============================================================================
// 1. EXHAUSTIVE JOURNEY DATA & WHATSAPP MOCKUP CONTENT
// ============================================================================

const JOURNEY_STAGES = [
  {
    id: 1,
    title: "Discovery & Lead Capture",
    timeline: "Days -14 to 0",
    icon: Search,
    touchpoint: "Initial Inquiry & Clinic Introduction",
    standard: "Calls missed after 8 PM. Leads drop off. Front desk spends 10 mins per call answering basic pricing queries and struggles to follow up with cold leads.",
    cura: "24/7 instant WhatsApp response. Answers clinic FAQs, showcases doctor profiles via IG reels, and dynamically books slots directly into the EMR.",
    mockup: [
      { dateDivider: "Initial Contact" },
      { sender: 'user', text: "Hi, do you offer EMI for IVF? I want to book a consultation." },
      { sender: 'bot', text: "Hello! Welcome to Canvas Future Fertility 🌸\n\nYes, we offer 0% EMI options starting at ₹8,500/month. Would you like to book an initial consultation with our senior specialist, Dr. Sharma?" },
      { sender: 'bot', text: "Please select an option below:", buttons: ["📅 Book Appointment", "💰 View EMI Plans", "🏥 Clinic Location"] },
      { sender: 'user', text: "📅 Book Appointment" },
      { sender: 'bot', text: "Great! Dr. Sharma is available tomorrow. Please pick a time:", buttons: ["10:30 AM", "02:00 PM", "04:15 PM"] },
      { sender: 'user', text: "10:30 AM" },
      { dateDivider: "1 Hour Later" },
      { sender: 'bot', text: "While you wait for your appointment tomorrow, take a quick 1-minute virtual tour of our state-of-the-art embryology lab and facilities! ✨" },
      { sender: 'bot', text: "📹 Virtual Clinic Tour", isVideo: true }
    ]
  },
  {
    id: 2,
    title: "Pre-Consultation",
    timeline: "Day 0",
    icon: FileText,
    touchpoint: "Digital Onboarding & Anxiety Reduction",
    standard: "Patient waits 30-45 mins filling out 5-page paper forms. High waiting room frustration leading to negative first impressions.",
    cura: "Sends a secure digital intake form via WhatsApp 24h prior. Patient uploads past reports. File is ready before they walk in. Google Maps API guides them.",
    mockup: [
      { dateDivider: "24 Hours Before Visit" },
      { sender: 'bot', text: "Your appointment is confirmed for tomorrow at 10:30 AM! 🎉" },
      { sender: 'bot', text: "To save you 30 minutes in the waiting room, please complete your secure digital registration and upload any past ultrasound or semen analysis reports." },
      { sender: 'bot', text: "Tap below to begin securely:", link: "📄 Start Registration (Secure)" },
      { sender: 'user', text: "Done. I have also attached my recent USG report." },
      { sender: 'bot', text: "Received! Your file is now synced to Dr. Sharma's dashboard." },
      { dateDivider: "2 Hours Before Visit" },
      { sender: 'bot', text: "We look forward to seeing you soon, Sarah! 🚗 Here is the Google Maps pin to our clinic parking. Call us if you need directions.", buttons: ["📍 Open in Maps", "📞 Call Front Desk"] }
    ]
  },
  {
    id: 3,
    title: "The First Consult",
    timeline: "Day 1",
    icon: Stethoscope,
    touchpoint: "Treatment Plan & Post-Visit Clarity",
    standard: "Patient leaves overwhelmed with medical jargon, AMH levels, and a heavy cost estimate. Forgets 80% of clinical instructions.",
    cura: "Instantly triggers a 'Post-Consult Summary'. Delivers a welcome video from the Doctor, a digital protocol copy, and assigns a dedicated digital care coordinator.",
    mockup: [
      { dateDivider: "Post-Consultation (Same Day)" },
      { sender: 'bot', text: "Hi Sarah, it was lovely having you at the clinic today! 🌟" },
      { sender: 'bot', text: "Dr. Sharma has prepared a quick 2-minute video summarizing your discussion about your AMH levels and the recommended Antagonist IVF protocol." },
      { sender: 'bot', text: "📹 Welcome & Protocol Summary", isVideo: true },
      { sender: 'bot', text: "I have also attached a secure PDF of your complete treatment and financial plan for your reference at home." },
      { sender: 'bot', text: "Treatment_Plan_Sarah.pdf", isPdf: true },
      { sender: 'bot', text: "I am CuRA, your dedicated 24/7 digital care coordinator. You can ask me questions anytime during your journey! 💙" }
    ]
  },
  {
    id: 4,
    title: "Decision Phase (FCDR Prevention)",
    timeline: "Days 2 to 14 (🚨 HIGH FCDR ZONE)",
    icon: BrainCircuit,
    touchpoint: "Consultative Storytelling & Nurturing",
    standard: "Clinics experience a 30-40% First Consultation Drop Rate (FCDR). Coordinators make manual calls that feel 'salesy' while patients are discussing finances.",
    cura: "Passive 14-Day WhatsApp Drip. Utilizes storytelling, IG Reels, Spotify playlists, and Zoom invites to build massive trust on autopilot without human intervention.",
    mockup: [
      { dateDivider: "Day 2: Empathy Check-In" },
      { sender: 'bot', text: "Hi Sarah 🌸 Taking the first step in IVF is a big family decision. How are you and your husband feeling today?" },
      { sender: 'user', text: "We are okay, just a bit overwhelmed with all the info." },
      { sender: 'bot', text: "That is completely normal! I'll share some gentle, bite-sized resources over the next few days so you don't feel rushed. 💙" },
      { dateDivider: "Day 4: Financial Transparency" },
      { sender: 'bot', text: "We know budgeting for IVF can be stressful. Did you know we offer 0% EMI options starting at just ₹8,500/month?" },
      { sender: 'bot', text: "Tap below to instantly calculate your EMI.", buttons: ["💸 EMI Calculator", "📞 Request Callback"] },
      { dateDivider: "Day 8: Engaging the Partner" },
      { sender: 'bot', text: "IVF is a team effort! 🤝 Here is a great post for your husband on 5 simple ways he can support you during the cycle." },
      { sender: 'bot', text: "IG Carousel: Guide for IVF Partners", link: "instagram.com/p/partner-guide" },
      { dateDivider: "Day 12: Storytelling & Conversion" },
      { sender: 'bot', text: "Meet Priya. Like you, she sat in our clinic a year ago feeling hesitant. Watch her 60-second journey to holding her baby boy. 🍼✨" },
      { sender: 'bot', text: "📹 Priya's IVF Journey", isVideo: true },
      { sender: 'user', text: "Hi, my husband and I have decided to go ahead with Dr. Sharma." },
      { sender: 'bot', text: "That is wonderful news, Sarah! 🎉 Shall I go ahead and block a tentative Day-2 scan slot for you?", buttons: ["📅 Schedule Day-2 Scan"] }
    ]
  },
  {
    id: 5,
    title: "Pre-Treatment Prep",
    timeline: "Days 15 to 20",
    icon: FileDown,
    touchpoint: "Holistic Preparation & Diagnostics",
    standard: "Patients repeatedly call to check if blood reports are ready, or ask how to take basic oral priming meds.",
    cura: "Automatically pushes lab reports (PDFs) from EMR. Sends daily, simple reminders for priming medications, plus diet and yoga resources.",
    mockup: [
      { dateDivider: "Day 15: Diagnostics" },
      { sender: 'bot', text: "Good afternoon Sarah! Your pre-IVF viral markers and baseline blood work results are ready." },
      { sender: 'bot', text: "Baseline_Labs_Sarah.pdf", isPdf: true },
      { dateDivider: "Day 16: Holistic Health" },
      { sender: 'bot', text: "Preparing your body naturally improves outcomes! Here is a beautiful, easy-to-read Diet & Fertility Yoga guide created by our nutritionist." },
      { sender: 'bot', text: "IVF_Prep_Lifestyle_Guide.pdf", isPdf: true },
      { dateDivider: "Day 20: Medication Reminder" },
      { sender: 'bot', text: "Everything looks perfectly normal. ✅\n\nAs a reminder, please take your priming medication (Novelon) tonight at 8:00 PM." },
      { sender: 'bot', text: "Tap to confirm once taken:", buttons: ["💊 Dose Taken"] },
      { sender: 'user', text: "💊 Dose Taken" }
    ]
  },
  {
    id: 6,
    title: "Ovarian Stimulation",
    timeline: "Days 21 to 32",
    icon: Pill,
    touchpoint: "Daily Hormone Tracking & Side Effects",
    standard: "Patients panic about self-injecting, mix up dosages, or call nurses at night regarding normal bloating side effects.",
    cura: "10 min warning with a 'How-to Inject' video. Mandates 'Confirm Dose' to log compliance. Auto-answers side-effect queries via NLP.",
    mockup: [
      { dateDivider: "Day 1 of Stimulation" },
      { sender: 'bot', text: "🚨 MEDICATION REMINDER 🚨\n\nYour Gonal-F (300 IU) injection is due in 10 minutes at 8:00 PM." },
      { sender: 'bot', text: "Here is a quick refresher on how to prepare and administer the Gonal-F pen:" },
      { sender: 'bot', text: "📹 How to inject Gonal-F", isVideo: true },
      { sender: 'bot', text: "This is crucial for your cycle. Please confirm immediately once administered.", buttons: ["✅ Confirmed Taken", "📞 I Need Help"] },
      { sender: 'user', text: "✅ Confirmed Taken" },
      { dateDivider: "Day 6 of Stimulation" },
      { sender: 'user', text: "I'm feeling very bloated and heavy today. Should I be worried?" },
      { sender: 'bot', text: "Mild to moderate bloating is very common and actually a good sign that your follicles are growing! Drink plenty of electrolytes. If the pain becomes severe, let me know immediately." }
    ]
  },
  {
    id: 7,
    title: "The Trigger Shot",
    timeline: "Day 33 (🚨 CRITICAL CLINICAL ZONE)",
    icon: AlertTriangle,
    touchpoint: "Final Maturation Injection",
    standard: "Relies on patient alarms. A missed trigger at 1:30 AM ruins the entire IVF cycle, costing time and clinic revenue.",
    cura: "Escalating Alarm Protocol. Sends a T-12h heads up. At T-0, if unconfirmed within 15 mins, initiates an automated phone call & nurse SMS.",
    mockup: [
      { dateDivider: "1:00 PM (12 Hours Prior)" },
      { sender: 'bot', text: "Sarah, tonight is the most important injection of your cycle: The Trigger Shot. It MUST be taken exactly at 1:30 AM tonight to prepare for surgery." },
      { dateDivider: "1:30 AM (Trigger Time)" },
      { sender: 'bot', text: "🚨 CRITICAL ACTION REQUIRED 🚨\n\nSarah, it is exactly 1:30 AM. It is time for your Trigger Shot (Ovitrelle)." },
      { sender: 'bot', text: "If unconfirmed in 15 minutes, the system will automatically call your phone.", buttons: ["✅ TRIGGER TAKEN", "🆘 EMERGENCY CALL"] },
      { sender: 'user', text: "✅ TRIGGER TAKEN" },
      { sender: 'bot', text: "Trigger logged at 1:32 AM. You are all set for egg retrieval! 💤" },
      { sender: 'bot', text: "⚠️ Remember: STRICT FASTING (No food or water) starting from 10:00 PM tomorrow night." }
    ]
  },
  {
    id: 8,
    title: "Oocyte Pick-Up (OPU)",
    timeline: "Day 35",
    icon: Clock,
    touchpoint: "Egg Retrieval Surgery & Discharge",
    standard: "Patients forget fasting rules. Post-op, they are groggy and lose their physical discharge prescription.",
    cura: "NPO reminders night before. Morning of: calm messaging. Post-op: pushes digital discharge summaries, diet plans, and introduces the embryologist.",
    mockup: [
      { dateDivider: "Surgery Morning" },
      { sender: 'bot', text: "Good morning Sarah! Deep breaths today. 🌻 Please head to the clinic 3rd floor ward. Remember, strictly no water/food." },
      { dateDivider: "Post-Surgery (4:00 PM)" },
      { sender: 'bot', text: "You did great today! 🌸 Dr. Sharma successfully retrieved 12 mature eggs." },
      { sender: 'bot', text: "Dr. Arun, our Chief Embryologist, has prepared a quick video explaining what happens next in the lab:" },
      { sender: 'bot', text: "📹 Meet your Embryologist", isVideo: true },
      { sender: 'bot', text: "Please rest, drink medical grade ORS to stay hydrated, and follow your digital discharge plan below.", buttons: ["View Discharge Plan"] }
    ]
  },
  {
    id: 9,
    title: "Embryology Lab",
    timeline: "Days 36 to 40",
    icon: Microscope,
    touchpoint: "Fertilization & Blastocyst Updates",
    standard: "Anxious patients flood clinic phone lines demanding to speak to the lab, annoying embryologists and causing operational drag.",
    cura: "Complete lab shield. Pulls secure data from EMR to send automated Day 1, Day 3, and Day 5 visual updates directly to WhatsApp.",
    mockup: [
      { dateDivider: "Day 1 Post-Retrieval" },
      { sender: 'bot', text: "🔬 LAB UPDATE: DAY 1 🔬\n\nGreat news! Out of the 12 eggs retrieved, 10 have successfully fertilized via ICSI. They are now safe in our incubators." },
      { dateDivider: "Day 3 Post-Retrieval" },
      { sender: 'bot', text: "🔬 LAB UPDATE: DAY 3 🔬\n\nYour embryos are dividing beautifully! 8 of them have reached the cleavage stage." },
      { sender: 'bot', text: "Here is a short video on how we 'Grade' your embryos:", link: "IG Reel: Embryo Grading" },
      { dateDivider: "Day 5 Post-Retrieval" },
      { sender: 'bot', text: "🔬 FINAL LAB UPDATE: DAY 5 🔬\n\nCongratulations! You have 4 beautiful Day-5 Blastocysts. They have been safely vitrified (frozen) for future transfer." },
      { sender: 'bot', text: "Embryo_Freeze_Report.pdf", isPdf: true }
    ]
  },
  {
    id: 10,
    title: "Embryo Transfer Prep",
    timeline: "Days 41 to 42",
    icon: CalendarClock,
    touchpoint: "Endometrial Preparation & Bladder Protocols",
    standard: "Confusion over progesterone timings (injections vs. gels) and improper bladder-filling on transfer day.",
    cura: "Sends step-by-step medication prep guides. Day-of gentle reminder specifically tracking the 'full bladder' requirement.",
    mockup: [
      { dateDivider: "2 Days Before Transfer" },
      { sender: 'bot', text: "Sarah, you are 48 hours away from your Frozen Embryo Transfer (FET)! 🌱" },
      { sender: 'bot', text: "Please continue your Progesterone support (Crinone Gel) exactly as prescribed. Do not skip a dose." },
      { dateDivider: "Morning of Transfer" },
      { sender: 'bot', text: "Today is the day! ✨" },
      { sender: 'bot', text: "⚠️ Important: Your transfer requires a FULL bladder for ultrasound visibility. Please drink 1 liter of water exactly 1 hour before your 11:00 AM appointment time.", buttons: ["Acknowledge"] },
      { sender: 'user', text: "Acknowledge" }
    ]
  },
  {
    id: 11,
    title: "Two-Week Wait (TWW)",
    timeline: "Days 43 to 56 (🚨 HIGH ANXIETY)",
    icon: HeartHandshake,
    touchpoint: "Implantation Waiting & Mental Health",
    standard: "Radio silence. Patients turn to Google, panic over mild cramps, and flood WhatsApp with non-emergencies. Risk of OHSS ignored.",
    cura: "14-day Empathetic Drip. NLP actively monitors replies. Flags 'severe bloating' instantly to doctor. Shares mindfulness podcasts.",
    mockup: [
      { dateDivider: "Day 2 of TWW" },
      { sender: 'bot', text: "The 'seed' is planted! 🌱 The Two-Week Wait can be stressful. Try this 10-min audio calming music playlist we curated for our patients:" },
      { sender: 'bot', text: "YouTube: TWW Calm Playlist", link: "youtube.com/playlist?list=tww-calm" },
      { dateDivider: "Day 6 of TWW" },
      { sender: 'user', text: "I am having some severe bloating and shortness of breath since last night." },
      { sender: 'bot', text: "🚨 RED FLAG DETECTED 🚨\n\nSarah, severe bloating and shortness of breath can be signs of hyperstimulation. I have instantly alerted Dr. Sharma." },
      { sender: 'bot', text: "Please sit down and rest. The duty doctor will call you within 5 minutes." },
      { dateDivider: "Day 12 of TWW" },
      { sender: 'bot', text: "You're almost there! 🌟 Please resist the urge to use home pregnancy kits, as medications can cause false positives/negatives. Trust the blood test on Day 14!" }
    ]
  },
  {
    id: 12,
    title: "Beta hCG Test",
    timeline: "Day 57",
    icon: LineChart,
    touchpoint: "Pregnancy Confirmation & Next Steps",
    standard: "High emotional volatility. Front desk struggles to handle communicating sensitive results manually over the phone.",
    cura: "Securely delivers PDF. Positive = celebratory message + early pregnancy diet. Negative = empathetic doctor video & counseling booking.",
    mockup: [
      { dateDivider: "Test Day Morning" },
      { sender: 'bot', isImage: true, imageUrl: "https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&q=80&w=400&h=300" },
      { sender: 'bot', text: "Good morning Sarah ma'am. Reminder: Your Beta hCG blood test is scheduled for today.\n\nPlease visit the clinic lab empty stomach before 10:30 AM." },
      { dateDivider: "Results (5:00 PM)" },
      { sender: 'bot', text: "Your Beta hCG blood test results have been securely uploaded from the lab." },
      { sender: 'bot', text: "Beta_hCG_Results.pdf", isPdf: true },
      { sender: 'bot', text: "🎉 CONGRATULATIONS! 🎉\n\nYour hCG levels are wonderfully high (450 mIU/mL). You are pregnant! Dr. Sharma and the entire Canvas Future team are overjoyed for you. ❤️" },
      { sender: 'bot', text: "Here is your critical Early Pregnancy Do's & Don'ts guide:", buttons: ["View Diet Guide"] }
    ]
  },
  {
    id: 13,
    title: "Early Pregnancy",
    timeline: "Day 65+",
    icon: Baby,
    touchpoint: "Clinical Continuity & Retention",
    standard: "Patients feel abandoned after a positive test and often migrate to a different maternity hospital, losing lifetime LTV.",
    cura: "Continues engagement. Sends weekly fetal developmental updates and seamlessly auto-schedules the 6-week heartbeat scan.",
    mockup: [
      { dateDivider: "Week 5" },
      { sender: 'bot', text: "Happy 5 Weeks! 🍼\n\nYour baby is now the size of an appleseed. You might start feeling morning sickness. Ginger tea works wonders! 🫖" },
      { dateDivider: "Week 6" },
      { sender: 'bot', text: "It is time for your Clinical Viability (Heartbeat) Scan with Dr. Sharma! 💓" },
      { sender: 'bot', text: "We have tentatively reserved a slot for you on Tuesday at 3:00 PM. Would you like to confirm this slot?", buttons: ["✅ Confirm Scan Slot", "Change Time"] },
      { sender: 'user', text: "✅ Confirm Scan Slot" },
      { sender: 'bot', text: "Confirmed! We can't wait to show you the heartbeat." }
    ]
  }
];

// ============================================================================
// 2. WHATSAPP MOCKUP COMPONENT
// ============================================================================

const WhatsAppMockup = ({ messages, brandName = "Canvas Future Fertility" }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <div className="w-[320px] h-[650px] bg-black rounded-[3rem] p-1.5 shadow-2xl relative border-[2px] border-slate-800 shrink-0 mx-auto transform transition-all duration-500 hover:scale-[1.02]">
      {/* Hardware Buttons */}
      <div className="absolute top-28 -left-1.5 w-1.5 h-10 bg-slate-800 rounded-l-md"></div>
      <div className="absolute top-40 -left-1.5 w-1.5 h-10 bg-slate-800 rounded-l-md"></div>
      <div className="absolute top-32 -right-1.5 w-1.5 h-14 bg-slate-800 rounded-r-md"></div>
      
      {/* Screen */}
      <div className="w-full h-full bg-[#EFE6DD] rounded-[2.6rem] overflow-hidden relative flex flex-col">
        {/* Dynamic Island */}
        <div className="absolute top-2 inset-x-0 mx-auto w-[90px] h-[26px] bg-black rounded-full z-40 flex items-center justify-between px-2">
          <div className="w-2 h-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-full"></div>
          <div className="w-2 h-2 bg-emerald-500 rounded-full opacity-40 blur-[1px]"></div>
        </div>

        {/* Status Bar */}
        <div className="h-10 w-full pt-3 px-6 flex justify-between items-center text-[12px] font-bold z-30 bg-transparent absolute top-0 text-black">
          <span className="tracking-tight mt-0.5">09:41</span>
          <div className="flex gap-1.5 items-center mr-0 mt-0.5">
            <Signal size={14} className="fill-current" strokeWidth={2.5} />
            <Wifi size={14} className="fill-current" strokeWidth={2.5} />
            <Battery size={16} className="fill-current opacity-90" />
          </div>
        </div>

        {/* WhatsApp Header */}
        <div className="bg-[#f6f6f6]/95 backdrop-blur-md text-black px-2 pt-11 pb-2 flex items-center justify-between z-20 border-b border-gray-300">
          <div className="flex items-center gap-1">
            <button className="text-[#007AFF] flex items-center"><ChevronLeft size={28} strokeWidth={2.5} /></button>
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-600 rounded-full flex items-center justify-center overflow-hidden ml-0.5 shadow-sm text-white font-bold text-xs">
              CF
            </div>
            <div className="ml-2 flex flex-col justify-center">
              <h2 className="font-semibold text-[15px] flex items-center gap-1 leading-none mb-1">
                {brandName} 
                <div className="bg-[#00A884] rounded-full w-[12px] h-[12px] flex items-center justify-center">
                  <CheckCheck size={8} className="text-white" strokeWidth={3} />
                </div>
              </h2>
              <p className="text-[11px] text-gray-500 leading-none">Official Business Account</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[#007AFF] pr-3">
            <Video size={24} strokeWidth={1.5} />
            <Phone size={22} strokeWidth={1.5} />
          </div>
        </div>

        {/* Chat Area */}
        <div ref={scrollRef} className="flex-1 bg-[#EFE6DD] p-3 overflow-y-auto relative custom-scrollbar flex flex-col gap-3" 
             style={{backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 10h10v10H10zM30 30h10v10H30z' fill='%23000000' fill-opacity='0.035' fill-rule='evenodd'/%3E%3C/svg%3E")`, backgroundSize: '120px', scrollBehavior: 'smooth'}}>
          
          <div className="flex justify-center mt-2 mb-1">
            <span className="bg-[#f0f2f5]/90 backdrop-blur-sm text-[#54656f] text-[11px] font-medium px-3 py-1 rounded-lg shadow-sm">Today</span>
          </div>
          <div className="flex justify-center mb-3">
            <div className="bg-[#FCE19B] text-[#8C6D1F] text-[11px] px-4 py-2.5 rounded-lg shadow-sm text-center max-w-[95%] leading-snug">
              🔒 Messages and calls are end-to-end encrypted. No one outside of this chat can read or listen to them.
            </div>
          </div>

          {messages.map((msg, idx) => {
            if (msg.dateDivider) {
              return (
                <div key={idx} className="flex justify-center mt-3 mb-2 w-full animate-in fade-in duration-500" style={{ animationDelay: `${idx * 150}ms`, animationFillMode: 'both' }}>
                  <span className="bg-[#f0f2f5]/90 backdrop-blur-sm text-[#54656f] text-[11px] font-medium px-3 py-1 rounded-lg shadow-sm">{msg.dateDivider}</span>
                </div>
              );
            }

            const isUser = msg.sender === 'user';
            const isSystemAlert = msg.text && msg.text.includes("🚨");
            
            return (
              <div key={idx} className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} animate-in fade-in slide-in-from-bottom-4 duration-500`} style={{ animationDelay: `${idx * 150}ms`, animationFillMode: 'both' }}>
                <div className={`relative max-w-[85%] rounded-2xl px-3 py-2 text-[14.5px] shadow-sm leading-snug ${isUser ? 'bg-[#E1F7CB] rounded-br-sm' : isSystemAlert ? 'bg-rose-50 border border-rose-200 rounded-tl-sm' : 'bg-white rounded-tl-sm'}`}>
                  
                  {msg.isImage && (
                    <div className="w-full h-36 bg-slate-200 rounded-xl mb-2 overflow-hidden border border-black/10">
                      <img src={msg.imageUrl} className="w-full h-full object-cover" alt="Attached graphic" />
                    </div>
                  )}

                  {msg.isVideo && (
                    <div className="w-full h-32 bg-slate-800 rounded-xl mb-2 flex items-center justify-center relative overflow-hidden group cursor-pointer border border-black/10">
                      <img src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&q=80&w=400&h=300" className="absolute inset-0 w-full h-full object-cover opacity-60" alt="Video thumbnail" />
                      <div className="w-12 h-12 bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center z-10 group-hover:bg-white/40 transition">
                        <div className="w-0 h-0 border-t-8 border-t-transparent border-l-12 border-l-white border-b-8 border-b-transparent ml-1"></div>
                      </div>
                    </div>
                  )}

                  {msg.isPdf && (
                    <div className="bg-slate-50 rounded-xl p-2 mb-2 flex items-center gap-3 border border-slate-200 shadow-sm cursor-pointer hover:bg-slate-100 transition">
                      <div className="bg-rose-500 text-white p-2.5 rounded-lg"><FileText size={20} /></div>
                      <div className="flex-1 min-w-0 pr-2">
                        <p className="text-[13px] font-semibold text-slate-800 truncate leading-tight">{msg.text}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">PDF Document • 1.2 MB</p>
                      </div>
                    </div>
                  )}

                  {(!msg.isPdf && msg.text) && (
                    <span className={`${isSystemAlert ? 'text-rose-900 font-medium' : 'text-[#111b21]'} whitespace-pre-wrap`}>{msg.text}</span>
                  )}
                  
                  <div className={`flex justify-end items-center gap-1 mt-1 -mb-1 ${msg.isPdf || msg.isVideo || msg.isImage ? 'pt-1' : ''}`}>
                    <span className="text-[10px] text-gray-500 opacity-90">09:41</span>
                    {isUser && <CheckCheck size={14} className="text-[#53bdeb]" />}
                  </div>
                </div>

                {msg.link && (
                  <div className="mt-1.5 w-full max-w-[85%]">
                    <button className="bg-white border border-gray-200 text-[#007AFF] shadow-sm text-[13px] font-medium py-2 px-3 rounded-xl hover:bg-gray-50 transition text-left w-full flex items-center gap-2">
                       <ImageIcon size={14}/> {msg.link}
                    </button>
                  </div>
                )}

                {msg.buttons && (
                  <div className="flex flex-col gap-1.5 mt-1.5 w-full max-w-[85%]">
                    {msg.buttons.map((btn, bIdx) => (
                      <button key={bIdx} className={`bg-white border text-[#007AFF] shadow-sm text-[13.5px] font-semibold py-2 px-3 rounded-xl hover:bg-gray-50 transition active:scale-95 text-center w-full flex justify-center items-center gap-1.5 ${btn.includes('EMERGENCY') ? 'border-rose-200 text-rose-600' : 'border-gray-200'}`}>
                        {btn}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Input Area */}
        <div className="bg-[#f6f6f6] px-2 pt-2 pb-6 flex items-end gap-2 relative z-10 border-t border-gray-300 shrink-0">
          <button className="text-[#007AFF] p-1.5 flex-shrink-0 mb-1"><Plus size={28} /></button>
          <div className="flex-1 bg-white rounded-full flex items-center px-3 py-2 border border-gray-300 shadow-sm mb-1 text-slate-400 text-[15px]">
            Reply...
          </div>
          <button className="text-[#007AFF] p-2 flex-shrink-0 mb-0.5"><Mic size={26} /></button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// 3. MAIN PRESENTATION COMPONENT
// ============================================================================

export default function JourneyPresentation() {
  const [activeStageId, setActiveStageId] = useState(1);
  const scrollContainerRef = useRef(null);

  const activeStage = JOURNEY_STAGES.find(s => s.id === activeStageId);
  const ActiveIcon = activeStage ? activeStage.icon : Activity;

  // Auto-scroll timeline to keep active item in view
  useEffect(() => {
    const container = scrollContainerRef.current;
    const activeElement = document.getElementById(`stage-btn-${activeStageId}`);
    
    if (container && activeElement) {
      const containerWidth = container.offsetWidth;
      const elementLeft = activeElement.offsetLeft;
      const elementWidth = activeElement.offsetWidth;
      
      container.scrollTo({
        left: elementLeft - (containerWidth / 2) + (elementWidth / 2),
        behavior: 'smooth'
      });
    }
  }, [activeStageId]);

  if (!activeStage) return null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-teal-500/30 flex flex-col">
      {/* Background Ambience */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40vw] h-[40vw] rounded-full bg-teal-600/20 blur-[120px] mix-blend-screen"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-blue-800/20 blur-[150px] mix-blend-screen"></div>
      </div>

      <div className="relative z-10 flex-1 flex flex-col h-screen overflow-hidden">
        
        {/* HEADER */}
        <header className="px-6 py-5 border-b border-white/10 bg-slate-950/80 backdrop-blur-md shrink-0">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400 text-[10px] font-bold uppercase tracking-widest mb-2">
                <Activity size={12} /> Live Interactive Presentation
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-white leading-tight">
                CuRA AI: Complete Patient Journey Map
              </h1>
            </div>
            <div className="text-right hidden md:block">
              <p className="text-sm text-slate-400">Optimized for High-Volume Indian IVF Clinics</p>
              <p className="text-xs text-teal-500 font-medium">Standard 60-Day Cycle Architecture</p>
            </div>
          </div>
        </header>

        {/* TIMELINE NAVIGATOR (Horizontal Scroll) */}
        <div className="bg-slate-900/50 border-b border-white/5 shrink-0 relative">
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-slate-950 to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-slate-950 to-transparent z-10 pointer-events-none"></div>
          
          <div 
            ref={scrollContainerRef}
            className="flex overflow-x-auto hide-scrollbar scroll-smooth items-center px-6 py-4 max-w-7xl mx-auto gap-3"
          >
            {JOURNEY_STAGES.map((stage) => {
              const isActive = stage.id === activeStageId;
              const isPast = stage.id < activeStageId;
              const Icon = stage.icon;
              
              return (
                <button
                  key={stage.id}
                  id={`stage-btn-${stage.id}`}
                  onClick={() => setActiveStageId(stage.id)}
                  className={`flex flex-col min-w-[160px] p-3 rounded-xl border text-left transition-all duration-300 relative overflow-hidden group ${
                    isActive 
                      ? 'bg-teal-500/20 border-teal-500/50 shadow-[0_0_20px_rgba(20,184,166,0.15)] scale-105' 
                      : isPast
                        ? 'bg-white/5 border-white/10 hover:bg-white/10 opacity-70'
                        : 'bg-white/5 border-white/10 hover:bg-white/10 opacity-50 hover:opacity-100'
                  }`}
                >
                  {isActive && <div className="absolute top-0 left-0 w-full h-1 bg-teal-400"></div>}
                  <div className="flex justify-between items-start mb-2">
                    <div className={`p-1.5 rounded-lg ${isActive ? 'bg-teal-500/30 text-teal-300' : 'bg-slate-800 text-slate-400'}`}>
                      <Icon size={16} />
                    </div>
                    <span className={`text-[10px] font-bold ${isActive ? 'text-teal-400' : 'text-slate-500'}`}>STAGE {stage.id}</span>
                  </div>
                  <h4 className={`text-sm font-bold truncate w-full ${isActive ? 'text-white' : 'text-slate-300'}`}>
                    {stage.title}
                  </h4>
                  <p className="text-[10px] text-slate-500 mt-1">{stage.timeline}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* MAIN CONTENT SPLIT VIEW */}
        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row max-w-7xl mx-auto w-full">
          
          {/* Left Column: Text Details */}
          <div className="flex-1 p-6 lg:p-10 overflow-y-auto custom-scrollbar flex flex-col">
            <div className="animate-in fade-in slide-in-from-bottom-8 duration-500 key={activeStage.id} flex-1">
              
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-teal-500/20 text-teal-400 rounded-2xl flex items-center justify-center border border-teal-500/30 shadow-[0_0_15px_rgba(20,184,166,0.2)]">
                  <ActiveIcon size={24} />
                </div>
                <div>
                  <p className="text-teal-400 text-sm font-bold tracking-wider uppercase">{activeStage.timeline}</p>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-white">{activeStage.title}</h2>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-8 flex items-start gap-4">
                <div className="mt-1 text-blue-400"><User size={20}/></div>
                <div>
                  <h4 className="text-xs text-slate-400 uppercase font-bold tracking-wider mb-1">Patient Touchpoint</h4>
                  <p className="text-lg text-slate-200 font-medium">{activeStage.touchpoint}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Standard Process Block */}
                <div className="bg-rose-950/20 border border-rose-500/20 rounded-2xl p-6 relative overflow-hidden group hover:border-rose-500/40 transition">
                  <div className="absolute top-0 left-0 w-1 h-full bg-rose-500/50"></div>
                  <div className="flex items-center gap-2 mb-4">
                    <XCircle size={18} className="text-rose-400" />
                    <h3 className="text-rose-300 font-bold uppercase tracking-wider text-xs">Standard Clinic Process</h3>
                  </div>
                  <p className="text-rose-100/80 leading-relaxed text-sm">{activeStage.standard}</p>
                </div>

                {/* CuRA AI Block */}
                <div className="bg-teal-950/20 border border-teal-500/30 rounded-2xl p-6 relative overflow-hidden group hover:border-teal-500/50 shadow-[0_0_20px_rgba(20,184,166,0.05)] transition">
                  <div className="absolute top-0 left-0 w-1 h-full bg-teal-500"></div>
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle2 size={18} className="text-teal-400" />
                    <h3 className="text-teal-300 font-bold uppercase tracking-wider text-xs">CuRA AI Improvement</h3>
                  </div>
                  <p className="text-teal-50/90 leading-relaxed font-medium">{activeStage.cura}</p>
                </div>
              </div>

              {/* Navigation Controls */}
              <div className="flex justify-between items-center mt-auto pt-8 border-t border-white/10">
                <button 
                  onClick={() => setActiveStageId(Math.max(1, activeStageId - 1))}
                  disabled={activeStageId === 1}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white disabled:opacity-30 disabled:cursor-not-allowed transition"
                >
                  <ChevronLeft size={18} /> Previous Stage
                </button>
                <div className="text-slate-500 text-sm font-medium">
                  {activeStageId} of {JOURNEY_STAGES.length}
                </div>
                <button 
                  onClick={() => setActiveStageId(Math.min(JOURNEY_STAGES.length, activeStageId + 1))}
                  disabled={activeStageId === JOURNEY_STAGES.length}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-teal-600 hover:bg-teal-500 text-white font-bold shadow-lg shadow-teal-900/50 disabled:opacity-30 disabled:cursor-not-allowed transition"
                >
                  Next Stage <ChevronRight size={18} />
                </button>
              </div>

            </div>
          </div>

          {/* Right Column: iPhone Mockup */}
          <div className="w-full lg:w-[450px] bg-slate-900/30 border-l border-white/10 flex items-center justify-center p-6 lg:p-0 shrink-0 relative overflow-hidden">
             {/* Decorative glow behind phone */}
             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[500px] bg-teal-500/20 blur-[100px] rounded-full pointer-events-none"></div>
             
             <div key={`mockup-${activeStageId}`} className="animate-in zoom-in-95 fade-in duration-500 relative z-10 w-full flex justify-center">
               <WhatsAppMockup messages={activeStage.mockup} />
             </div>
          </div>

        </div>

      </div>

      {/* Global CSS for hiding scrollbars but keeping functionality */}
      <style dangerouslySetInnerHTML={{__html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
      `}} />
    </div>
  );
}